import { useEffect, useState, type ReactNode } from "react";
import { Check, ChevronLeft, ChevronRight, ImagePlay, KeyRound, Layers, Link2, Pencil, Plus, Trash2 } from "lucide-react";
import { HermesError, customEndpoints, deleteCustomEndpoint, providerMatches, type CustomEndpoint, type ModelOptionsResult, type ModelProvider } from "@timibot/core";
import { useApp } from "../../context";
import type { ChatStore } from "../../state/chat";
import { Dialog, Menu, PlainRow, SearchField, SectionTitle, Sheet, Spinner, useLongPress, useMenu } from "../../ui/components";
import { AuxiliaryModelsPage, CustomEndpointSheet, MediaGenerationPage, type Nav } from "./ModelServices";
import { ModelServiceNames } from "./serviceNames";
import "./chat.css";

/**
 * 模型：切换、设为默认、添加自己的 API Key（对应 iOS 的 ModelPickerSheet）。
 * - 从对话里打开：切换这段对话的模型，同时设为以后新对话的默认（Hermes 官方 `--global`）
 * - 从设置/机器人打开（chat 为空）：只设置新对话的默认模型
 * - 上面“添加模型服务”：填 API Key（密钥存在主机上），或者添加自定义接口
 * - 下面还有“辅助模型”和“生图与生视频”（见 ModelServices.tsx），设置的是当前这个 Bot；访客看不到
 */
export function ModelPickerSheet({ open, onClose, chat, bot, title }: {
  open: boolean; onClose: () => void; chat?: ChatStore;
  /** 从机器人编辑页打开：设置这个 Bot（默认 Bot 的 profile 为空）的默认模型 */
  bot?: { profile?: string }; title?: string;
}) {
  const app = useApp();
  const profile = bot ? bot.profile : (chat?.profile ?? app.selectedProfile);
  const [options, setOptions] = useState<ModelOptionsResult>();
  const [error, setError] = useState<string>();
  const [search, setSearch] = useState("");
  /** 压进来的下一页（添加服务、辅助模型、生图…），最上面那页显示，下面的留着状态 */
  const [stack, setStack] = useState<{ id: number; title: string; node: ReactNode }[]>([]);
  const [endpoints, setEndpoints] = useState<CustomEndpoint[]>([]);
  const [editing, setEditing] = useState<{ endpoint?: CustomEndpoint }>();
  const [expandedProviders, setExpanded] = useState<Set<string>>(new Set());
  const [confirm, setConfirm] = useState<{ model: string; provider: string; message: string }>();
  const [adding, setAdding] = useState<ModelProvider>();
  const [key, setKey] = useState("");
  const [removing, setRemoving] = useState<ModelProvider>();
  const menu = useMenu();
  const [menuProvider, setMenuProvider] = useState<ModelProvider>();

  const load = async () => {
    if (!app.api) return;
    try {
      setOptions(await app.api.modelOptions(chat?.liveSessionID, profile, true));
      setError(undefined);
      if (!app.isDemo) setEndpoints(await customEndpoints(app.api, profile).catch(() => []));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  useEffect(() => {
    if (!open) return;
    setStack([]);
    setSearch("");
    void load();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const currentModel = chat?.info.model ?? options?.model;
  const currentProvider = chat?.info.provider ?? options?.provider;
  // moa（多模型混合）是高级用法，普通用户用不上
  const configured = (options?.providers ?? [])
    .filter((p) => p.authenticated !== false && (p.models ?? []).length > 0 && p.slug !== "moa")
    .sort((a, b) => (providerMatches(a, currentProvider) ? 0 : 1) - (providerMatches(b, currentProvider) ? 0 : 1));
  const addable = (options?.providers ?? [])
    .filter((p) => p.authenticated === false && p.auth_type === "api_key" && p.slug !== "custom")
    .sort((a, b) => ModelServiceNames.rank(a) - ModelServiceNames.rank(b));

  const nav: Nav = {
    push: (title, node) => setStack((s) => [...s, { id: Date.now() + Math.random(), title, node }]),
    pop: () => setStack((s) => s.slice(0, -1)),
  };
  const endpointFor = (p: ModelProvider) => endpoints.find((e) => providerMatches(p, e.id) || e.name === p.name);

  const filtered = (p: ModelProvider): string[] => {
    const featured = p.featured_models ?? [];
    const all = featured.length && !search ? [...featured, ...(p.models ?? []).filter((m) => !featured.includes(m))] : (p.models ?? []);
    const q = search.toLowerCase();
    return q ? all.filter((m) => m.toLowerCase().includes(q)) : all;
  };

  const pick = async (model: string, provider: string, confirmExpensive: boolean) => {
    const api = app.api;
    if (!api) return;
    try {
      if (chat) {
        const sid = await chat.ensureSession();
        const r = await api.switchModel(sid, model, provider, true, confirmExpensive);
        if (r.confirm_required) { setConfirm({ model, provider, message: r.confirm_message ?? "这个模型价格较高，确定切换吗？" }); return; }
        chat.info = r.info ? { ...chat.info, ...stripUndefined(r.info) } : { ...chat.info, model, provider };
        app.touch();
        if (r.warning) app.showToast(r.warning);
      } else {
        const message = await setDefault(model, provider, confirmExpensive);
        if (message) { setConfirm({ model, provider, message }); return; }
        app.showToast(`新对话将使用 ${model}`);
      }
      // 默认模型变了：刷新机器人信息，新对话标题下显示的模型名跟着变
      void app.profiles.refresh(app.api);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  /** 设默认模型：先用网页后台的接口；自定义地址这类它不认的服务，走临时会话 + `--global` */
  const setDefault = async (model: string, provider: string, confirmExpensive: boolean): Promise<string | undefined> => {
    const api = app.api!;
    try {
      return await api.setDefaultModel(model, provider, profile, confirmExpensive);
    } catch (e) {
      if (!(e instanceof HermesError && e.kind === "http" && e.status === 400)) throw e;
      const snap = await api.createSession(profile);
      try {
        const r = await api.switchModel(snap.session_id, model, provider, true, confirmExpensive);
        return r.confirm_required ? (r.confirm_message ?? "这个模型价格较高，确定使用吗？") : undefined;
      } finally {
        api.closeSession(snap.session_id).catch(() => {});
      }
    }
  };

  const saveKey = async (p: ModelProvider, value: string) => {
    try {
      await app.api?.saveModelKey(p.slug, value.trim(), profile);
      app.showToast(`已添加 ${ModelServiceNames.title(p)}`);
      setStack([]);
      await load();
    } catch (e) {
      setError(`添加失败：${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const remove = async (p: ModelProvider) => {
    try {
      const endpoint = p.is_user_defined ? endpointFor(p) : undefined;
      if (endpoint) await deleteCustomEndpoint(app.api!, endpoint.id, profile);
      else await app.api?.disconnectModelProvider(p.slug, profile);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const sheetTitle = title ?? (chat ? "切换模型" : "默认模型");
  const top = stack[stack.length - 1];
  const chevron = <ChevronRight size={14} className="t-3" />;

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={top?.title ?? sheetTitle}
      leading={top
        ? <button className="sheet-done row" onClick={nav.pop}><ChevronLeft size={18} />返回</button>
        : <button className="sheet-done" onClick={onClose}>关闭</button>}
    >
      <div className="model-list" hidden={!!top}>
          <div className="model-search"><SearchField placeholder="搜索模型" value={search} onChange={setSearch} /></div>
          {options && !search && !app.isGuest && (
            <PlainRow icon={<Plus size={16} />} title="添加模型服务" trailing={chevron}
              onClick={() => nav.push("添加模型服务", <AddServiceList providers={addable} onPick={(p) => { setKey(""); setAdding(p); }} onCustom={() => setEditing({})} />)} />
          )}
          {options && !search && !app.isGuest && !app.isDemo && (
            <>
              <PlainRow icon={<Layers size={16} />} title="辅助模型" trailing={chevron}
                onClick={() => nav.push("辅助模型", <AuxiliaryModelsPage profile={profile} providers={configured} nav={nav} />)} />
              <PlainRow icon={<ImagePlay size={16} />} title="生图与生视频" trailing={chevron}
                onClick={() => nav.push("生图与生视频", <MediaGenerationPage profile={profile} nav={nav} />)} />
            </>
          )}
          {error && <div className="t-footnote t-danger model-error">{error}</div>}
          {!options && !error && <div className="center-pad"><Spinner /></div>}
          {configured.map((p) => {
            const models = filtered(p);
            if (!models.length) return null;
            const isOpen = expandedProviders.has(p.slug) || !!search;
            return (
              <ProviderSection
                key={p.slug}
                provider={p}
                models={isOpen ? models : models.slice(0, 8)}
                total={models.length}
                isOpen={isOpen}
                showToggle={models.length > 8 && !search}
                onToggle={() => setExpanded((s) => { const n = new Set(s); if (n.has(p.slug)) n.delete(p.slug); else n.add(p.slug); return n; })}
                isCurrent={(m) => m === currentModel && providerMatches(p, currentProvider)}
                onPick={(m) => void pick(m, p.slug, false)}
                onMenu={app.isGuest ? undefined : (pos) => { setMenuProvider(p); menu.show(pos); }}
              />
            );
          })}
      </div>
      {stack.map((p) => <div key={p.id} hidden={p !== top}>{p.node}</div>)}
      <Menu open={menu.open} anchor={menu.anchor} onClose={menu.close} align="start" width={200}
        items={[
          ...(menuProvider?.is_user_defined && endpointFor(menuProvider)
            ? [{ label: "编辑接口", icon: <Pencil size={16} />, onSelect: () => setEditing({ endpoint: endpointFor(menuProvider) }) }] : []),
          { label: "移除这个服务", icon: <Trash2 size={16} />, destructive: true, onSelect: () => setRemoving(menuProvider) },
        ]} />
      <CustomEndpointSheet open={editing !== undefined} existing={editing?.endpoint} profile={profile}
        onClose={() => setEditing(undefined)} onSaved={() => { setStack([]); void load(); }} />
      <Dialog
        open={confirm !== undefined}
        title="确认使用"
        message={confirm?.message}
        onClose={() => setConfirm(undefined)}
        buttons={[{ title: "取消", role: "cancel" }, { title: "继续", onClick: () => { if (confirm) void pick(confirm.model, confirm.provider, true); } }]}
      />
      <Dialog
        open={adding !== undefined}
        title={`添加 ${adding ? ModelServiceNames.title(adding) : ""}`}
        message="Key 只保存在你自己的主机上（Hermes 的 .env），不经过任何第三方。"
        onClose={() => setAdding(undefined)}
        input={{ value: key, onChange: setKey, placeholder: adding?.key_env ?? "API Key", secure: true }}
        buttons={[{ title: "取消", role: "cancel" }, { title: "保存", onClick: () => { if (adding && key.trim()) void saveKey(adding, key); } }]}
      />
      <Dialog
        open={removing !== undefined}
        title={`移除 ${removing ? ModelServiceNames.title(removing) : ""}？`}
        onClose={() => setRemoving(undefined)}
        buttons={[{ title: "取消", role: "cancel" }, { title: "移除", role: "destructive", onClick: () => { if (removing) void remove(removing); } }]}
      />
    </Sheet>
  );
}

function ProviderSection({ provider, models, total, isOpen, showToggle, onToggle, isCurrent, onPick, onMenu }: {
  provider: ModelProvider; models: string[]; total: number; isOpen: boolean; showToggle: boolean; onToggle: () => void;
  isCurrent: (m: string) => boolean; onPick: (m: string) => void; onMenu?: (pos: { x: number; y: number }) => void;
}) {
  const press = useLongPress((pos) => onMenu?.(pos));
  return (
    <section className="model-section">
      <div {...(onMenu ? press : {})}>
        <SectionTitle title={ModelServiceNames.title(provider)} trailing={showToggle
          ? <button className="t-footnote t-2" onClick={onToggle}>{isOpen ? "收起" : `全部 ${total}`}</button> : undefined} />
      </div>
      {models.map((m) => (
        <button key={m} className={`model-row${isCurrent(m) ? " current" : ""}`} onClick={() => onPick(m)}>
          <span className="t-subheadline nowrap">{m}</span>
          {isCurrent(m) && <Check size={15} strokeWidth={2.6} />}
        </button>
      ))}
    </section>
  );
}

/** 添加模型服务：自定义接口，以及可以填 API Key 添加的服务 */
function AddServiceList({ providers, onPick, onCustom }: { providers: ModelProvider[]; onPick: (p: ModelProvider) => void; onCustom: () => void }) {
  const [search, setSearch] = useState("");
  const q = search.toLowerCase();
  const shown = providers.filter((p) => !q || ModelServiceNames.title(p).toLowerCase().includes(q) || p.slug.includes(q));
  return (
    <div className="model-list">
      <div className="model-search"><SearchField placeholder="搜索服务" value={search} onChange={setSearch} /></div>
      {!search && <PlainRow icon={<Link2 size={15} />} title="自定义接口" subtitle="OpenAI 兼容地址" onClick={onCustom} trailing={<Plus size={15} className="t-2" />} />}
      {shown.map((p) => (
        <PlainRow key={p.slug} icon={<KeyRound size={15} />} title={ModelServiceNames.title(p)} onClick={() => onPick(p)} trailing={<Plus size={15} className="t-2" />} />
      ))}
      {!shown.length && <div className="t-footnote t-2 model-error">没有可以直接填 Key 添加的服务。需要网页授权的服务，请在主机上运行 hermes model 配置。</div>}
    </div>
  );
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;
}
